import { Customer } from '../types/customer';
import { MaintenanceRecord } from '../utils/types';
import { getSystemSettings } from './systemSettingsService';

// واجهة بيانات الرسالة
export interface WhatsAppMessageData {
  customerName: string;
  customerPhone: string;
  deviceType: string;
  deviceBrand?: string;
  deviceSize?: string;
  cost?: string;
  orderId?: string;
  estimatedDate?: string;
}

// أنواع الرسائل المتاحة
export type WhatsAppMessageType = 'deviceReceived' | 'deviceInProgress' | 'deviceCompleted' | 'deviceUrgent';

// Interfaz para las plantillas de mensajes (للتوافق مع الكود القديم)
export interface WhatsAppTemplate {
  name: string;
  language: string;
  components: any[];
}

// Interfaz para la configuración de WhatsApp (للتوافق مع الكود القديم)
export interface WhatsAppConfig {
  enabled: boolean;
  apiKey: string;
  phoneNumberId: string;
  businessAccountId: string;
  accessToken: string;
}

// Interfaz para los grupos de clientes (للتوافق مع الكود القديم)
export enum CustomerGroup {
  MAINTENANCE = 'maintenance',
  POS = 'pos',
  ALL = 'all'
}

// Obtener la configuración de WhatsApp
export const getWhatsAppConfig = async (): Promise<WhatsAppConfig> => {
  try {
    // Obtener la configuración de WhatsApp desde localStorage
    const whatsappConfig = localStorage.getItem('whatsapp_config');

    if (whatsappConfig) {
      return JSON.parse(whatsappConfig);
    }

    // Configuración predeterminada
    return {
      enabled: false,
      apiKey: '',
      phoneNumberId: '',
      businessAccountId: '',
      accessToken: ''
    };
  } catch (error) {
    console.error('Error getting WhatsApp config:', error);
    return {
      enabled: false,
      apiKey: '',
      phoneNumberId: '',
      businessAccountId: '',
      accessToken: ''
    };
  }
};

// Guardar la configuración de WhatsApp
export const saveWhatsAppConfig = async (config: WhatsAppConfig): Promise<void> => {
  try {
    localStorage.setItem('whatsapp_config', JSON.stringify(config));
  } catch (error) {
    console.error('Error saving WhatsApp config:', error);
    throw error;
  }
};

// Enviar mensaje de WhatsApp
export const sendWhatsAppMessage = async (
  phoneNumber: string,
  message: string,
  templateName?: string
): Promise<boolean> => {
  try {
    const config = await getWhatsAppConfig();

    if (!config.enabled || !config.accessToken || !config.phoneNumberId) {
      console.warn('WhatsApp messaging is not configured or disabled');
      return false;
    }

    // Formatear el número de teléfono (eliminar espacios y asegurarse de que comience con el código de país)
    const formattedPhone = formatPhoneNumber(phoneNumber);

    if (!formattedPhone) {
      console.error('Invalid phone number:', phoneNumber);
      return false;
    }

    // Si se proporciona una plantilla, usar la API de plantillas
    if (templateName) {
      return await sendTemplateMessage(formattedPhone, templateName, config);
    }

    // De lo contrario, enviar un mensaje de texto simple
    return await sendTextMessage(formattedPhone, message, config);
  } catch (error) {
    console.error('Error sending WhatsApp message:', error);
    return false;
  }
};

/**
 * تنسيق رقم الهاتف للواتساب (النسخة الجديدة)
 * @param phone رقم الهاتف
 * @returns رقم الهاتف منسق للواتساب
 */
export const formatPhoneForWhatsApp = (phone: string): string => {
  // إزالة جميع الرموز والمسافات
  let cleanPhone = phone.replace(/\D/g, '');

  // إذا كان الرقم يبدأ بـ 0، استبدله بـ 20 (مصر)
  if (cleanPhone.startsWith('0')) {
    cleanPhone = '20' + cleanPhone.substring(1);
  }

  // إذا لم يبدأ برمز الدولة، أضف رمز مصر
  if (!cleanPhone.startsWith('20')) {
    cleanPhone = '20' + cleanPhone;
  }

  return cleanPhone;
};

/**
 * استبدال المتغيرات في نص الرسالة
 * @param template نص الرسالة مع المتغيرات
 * @param data بيانات الرسالة
 * @returns النص بعد استبدال المتغيرات
 */
export const replaceMessageVariables = (template: string, data: WhatsAppMessageData): string => {
  return template
    .replace(/{customerName}/g, data.customerName)
    .replace(/{deviceType}/g, data.deviceType)
    .replace(/{deviceBrand}/g, data.deviceBrand || data.deviceType || 'غير محدد')
    .replace(/{deviceSize}/g, data.deviceSize || 'غير محدد')
    .replace(/{cost}/g, data.cost || 'غير محدد')
    .replace(/{orderId}/g, data.orderId || 'غير محدد')
    .replace(/{estimatedDate}/g, data.estimatedDate || 'غير محدد');
};

/**
 * إنشاء رابط واتساب
 * @param phone رقم الهاتف
 * @param message نص الرسالة
 * @returns رابط الواتساب
 */
export const createWhatsAppLink = (phone: string, message: string): string => {
  const formattedPhone = formatPhoneForWhatsApp(phone);
  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${formattedPhone}?text=${encodedMessage}`;
};

// Formatear el número de teléfono para WhatsApp (النسخة القديمة للتوافق)
const formatPhoneNumber = (phone: string): string | null => {
  // Eliminar todos los caracteres no numéricos
  const cleanPhone = phone.replace(/\D/g, '');

  // Verificar si el número ya tiene el código de país
  if (cleanPhone.startsWith('2') && cleanPhone.length === 12) {
    return cleanPhone;
  }

  // Agregar el código de país de Egipto (20) si el número comienza con 0
  if (cleanPhone.startsWith('0') && cleanPhone.length === 11) {
    return '2' + cleanPhone.substring(1);
  }

  // Agregar el código de país de Egipto (20) si el número tiene 10 dígitos
  if (cleanPhone.length === 10) {
    return '20' + cleanPhone;
  }

  // Si el número ya tiene el formato correcto
  if (cleanPhone.length === 11) {
    return '2' + cleanPhone;
  }

  console.error('Invalid phone number format:', phone);
  return null;
};

// Enviar mensaje de texto simple usando enlaces directos de WhatsApp
const sendTextMessage = async (
  phoneNumber: string,
  message: string,
  _config: WhatsAppConfig // Prefijo con guion bajo para indicar que no se usa
): Promise<boolean> => {
  try {
    // Crear el enlace de WhatsApp
    const encodedMessage = encodeURIComponent(message);
    const whatsappLink = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;

    console.log(`WhatsApp link created: ${whatsappLink}`);

    // Abrir el enlace en una nueva ventana
    window.open(whatsappLink, '_blank');

    return true;
  } catch (error) {
    console.error('Error creating WhatsApp link:', error);
    return false;
  }
};

// Enviar mensaje con plantilla predefinida
const sendTemplateMessage = async (
  phoneNumber: string,
  templateName: string,
  config: WhatsAppConfig,
  templateData?: any
): Promise<boolean> => {
  try {
    // Obtener el mensaje de la plantilla
    let message = '';

    // Plantillas predefinidas
    switch (templateName) {
      case 'maintenance_completed':
        message = 'مرحباً, نود إبلاغكم أنه تم إصلاح جهازكم بنجاح ويمكنكم استلامه من فرعنا.';
        break;
      case 'maintenance_received':
        message = 'مرحباً, نؤكد لكم استلام جهازكم للصيانة. سنقوم بالتواصل معكم قريباً بخصوص حالة الصيانة.';
        break;
      case 'promotional':
        message = 'عرض خاص لعملائنا الكرام! استفد من خصم 10% على جميع خدمات الصيانة هذا الأسبوع.';
        break;
      default:
        message = templateData?.message || '';
    }

    // Si hay datos de plantilla, reemplazar variables
    if (templateData) {
      if (templateData.customerName) {
        message = message.replace('{customerName}', templateData.customerName);
      }
      if (templateData.maintenanceId) {
        message = message.replace('{maintenanceId}', templateData.maintenanceId);
      }
      if (templateData.deviceType) {
        message = message.replace('{deviceType}', templateData.deviceType);
      }
    }

    // Usar la función de envío de texto simple
    return await sendTextMessage(phoneNumber, message, config);
  } catch (error) {
    console.error('Error sending WhatsApp template message:', error);
    return false;
  }
};

/**
 * إرسال رسالة واتساب (النسخة الجديدة)
 * @param messageType نوع الرسالة
 * @param data بيانات الرسالة
 * @returns Promise<object> نتيجة الإرسال
 */
export const sendNewWhatsAppMessage = async (
  messageType: WhatsAppMessageType,
  data: WhatsAppMessageData
): Promise<{ success: boolean; link?: string; error?: string }> => {
  try {
    // الحصول على إعدادات النظام
    const settings = await getSystemSettings();

    // التحقق من تفعيل الواتساب
    if (!settings.whatsappEnabled) {
      return {
        success: false,
        error: 'خدمة الواتساب غير مفعلة في الإعدادات'
      };
    }

    // الحصول على نص الرسالة
    const messageTemplate = settings.whatsappMessages[messageType];
    if (!messageTemplate) {
      return {
        success: false,
        error: 'نص الرسالة غير موجود'
      };
    }

    // استبدال المتغيرات في النص
    const finalMessage = replaceMessageVariables(messageTemplate, data);

    // إنشاء رابط الواتساب
    const whatsappLink = createWhatsAppLink(data.customerPhone, finalMessage);

    // فتح الرابط في نافذة جديدة
    window.open(whatsappLink, '_blank');

    return {
      success: true,
      link: whatsappLink
    };

  } catch (error) {
    console.error('Error sending WhatsApp message:', error);
    return {
      success: false,
      error: 'حدث خطأ أثناء إرسال الرسالة'
    };
  }
};

/**
 * معاينة رسالة واتساب قبل الإرسال
 * @param messageType نوع الرسالة
 * @param data بيانات الرسالة
 * @returns Promise<string> نص الرسالة بعد التنسيق
 */
export const previewWhatsAppMessage = async (
  messageType: WhatsAppMessageType,
  data: WhatsAppMessageData
): Promise<string> => {
  try {
    const settings = await getSystemSettings();
    const messageTemplate = settings.whatsappMessages[messageType];

    if (!messageTemplate) {
      return 'نص الرسالة غير موجود';
    }

    return replaceMessageVariables(messageTemplate, data);
  } catch (error) {
    console.error('Error previewing WhatsApp message:', error);
    return 'حدث خطأ في معاينة الرسالة';
  }
};

/**
 * التحقق من صحة رقم الهاتف للواتساب
 * @param phone رقم الهاتف
 * @returns boolean صحيح أم لا
 */
export const isValidWhatsAppPhone = (phone: string): boolean => {
  const cleanPhone = phone.replace(/\D/g, '');
  return cleanPhone.length >= 10 && cleanPhone.length <= 15;
};

// Enviar mensaje de mantenimiento completado (النسخة القديمة للتوافق)
export const sendMaintenanceCompletedMessage = async (
  maintenance: MaintenanceRecord,
  customer: Customer
): Promise<boolean> => {
  try {
    const config = await getWhatsAppConfig();

    // Obtener el número de teléfono del cliente
    const phoneNumber = customer.phone || customer.alternatePhone;

    if (!phoneNumber) {
      console.error('Customer has no phone number:', customer.id);
      return false;
    }

    // Datos para el mensaje personalizado
    // (Estos datos ya se utilizan directamente en la creación del mensaje)

    // Crear un mensaje personalizado
    const message = `مرحباً ${customer.name},

نود إبلاغكم أنه تم إصلاح جهازكم بنجاح ويمكنكم استلامه من فرعنا.

رقم الصيانة: ${maintenance.id}
نوع الجهاز: ${maintenance.deviceType}

شكراً لاختياركم خدماتنا.`;

    // Enviar el mensaje
    return await sendTextMessage(formatPhoneNumber(phoneNumber) || phoneNumber, message, config);
  } catch (error) {
    console.error('Error sending maintenance completed message:', error);
    return false;
  }
};

// Enviar mensaje promocional a un grupo de clientes
export const sendPromotionalMessage = async (
  message: string,
  _customerGroup: CustomerGroup, // Prefijo con guion bajo para indicar que no se usa
  customers: Customer[]
): Promise<{ success: number; failed: number }> => {
  try {
    const config = await getWhatsAppConfig();

    let successCount = 0;
    let failedCount = 0;

    // Enviar mensaje a cada cliente
    for (const customer of customers) {
      // Obtener el número de teléfono del cliente
      const phoneNumber = customer.phone || customer.alternatePhone;

      if (!phoneNumber) {
        console.error('Customer has no phone number:', customer.id);
        failedCount++;
        continue;
      }

      // Personalizar el mensaje con el nombre del cliente
      const personalizedMessage = message.replace('{customerName}', customer.name);

      // Enviar el mensaje
      const success = await sendTextMessage(
        formatPhoneNumber(phoneNumber) || phoneNumber,
        personalizedMessage,
        config
      );

      if (success) {
        successCount++;
      } else {
        failedCount++;
      }
    }

    return { success: successCount, failed: failedCount };
  } catch (error) {
    console.error('Error sending promotional message:', error);
    return { success: 0, failed: 0 };
  }
};
